/**
 * Flat Drill-down — board "5 · Flat Drill-down". A side panel that shows ONE
 * level of the menu at a time (enter a folder → its contents, "Back to
 * <parent>" + the path above), under a view switch: Menu / Pinned / Recent /
 * Editors. The open screen sits beside it with a breadcrumb in its title whose
 * folder crumbs move the panel to that folder, and the open editors as a tray
 * underneath.
 *
 * View state only: which level the panel shows and which view is switched on.
 * Everything else — the current screen, pins, recents, editors, full screen —
 * is the shared model's.
 */
import { PanelLeft } from 'lucide-react'
import { useEffect, useId, useState, type KeyboardEvent } from 'react'
import { tw } from '../../testHooks'
import { EditorsList, EditorsTray } from '../nav/parts/EditorsList'
import { EmptyPane } from '../nav/parts/EmptyPane'
import { Breadcrumb, ScreenTitle } from '../nav/parts/ScreenTitle'
import { PinnedList, RecentList } from '../nav/parts/ShellLists'
import { ShellFrame } from '../nav/ShellFrame'
import type { RunNavItem } from '../nav/types'
import { S, iconButton } from '../nav/tokens'
import { LevelView } from './flat/LevelView'
import type { ShellLayoutProps } from './types'

type View = 'menu' | 'pinned' | 'recent' | 'editors'

/** The breadcrumb's first crumb: the top of the menu. Never a real node id
 *  (those come from application.json), so it cannot collide. */
const ROOT_CRUMB: RunNavItem = {
  id: '__shell_flat_menu__', parent_id: '', obj_type: '-', obj_name: '', title: 'Menu', icon: 'home',
}

export default function FlatLayout({
  model, appTitle, themeColor, compact = false, renderObject, headerExtras,
  emptyNavMessage, emptyPaneMessage,
}: ShellLayoutProps) {
  const current = model.current
  const full = model.fullScreen
  const [view, setView] = useState<View>('menu')
  // The drilled level: the folder the current screen sits in, until the
  // person drills elsewhere. Resolved against the tree on every render, so a
  // menu that changed under it (rights, a rebuild) falls back to the top.
  const parentIdOf = (item: RunNavItem | null): string | null => {
    if (!item) return null
    const path = model.pathOf(item)
    return path.length ? path[path.length - 1].id : null
  }
  const [levelId, setLevelId] = useState<string | null>(() => parentIdOf(current))
  // The current screen changed — from this panel, Pinned / Recent, a link, the
  // host — so show the folder it sits in.
  useEffect(() => { if (current) setLevelId(parentIdOf(current)) }, [current?.id]) // eslint-disable-line react-hooks/exhaustive-deps
  const levelNode = levelId ? model.tree.byId.get(levelId) : undefined
  const level = levelNode && levelNode.isContainer ? levelNode : null

  // "Open editors" from the cap notice: the Editors view.
  useEffect(() => {
    if (model.editorsListRequest) setView('editors')
  }, [model.editorsListRequest])
  const shownView: View = view === 'editors' && !model.editorsEnabled ? 'menu' : view

  const tabs: { id: View; label: string; count?: number }[] = [
    { id: 'menu', label: 'Menu' },
    { id: 'pinned', label: 'Pinned', count: model.pinned.length },
    { id: 'recent', label: 'Recent', count: model.recent.length },
    ...(model.editorsEnabled ? [{ id: 'editors' as View, label: 'Editors', count: model.editors.length }] : []),
  ]
  const uid = useId()
  const tabId = (v: View) => `${uid}-tab-${v}`
  const panelId = `${uid}-panel`
  const onTabKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const i = tabs.findIndex((t) => t.id === shownView)
    const next = e.key === 'ArrowRight' ? i + 1 : e.key === 'ArrowLeft' ? i - 1
      : e.key === 'Home' ? 0 : e.key === 'End' ? tabs.length - 1 : null
    if (next === null) return
    e.preventDefault()
    const t = tabs[(next + tabs.length) % tabs.length]
    setView(t.id)
    document.getElementById(tabId(t.id))?.focus()
  }

  const showFolder = (folder: RunNavItem) => {
    setView('menu')
    setLevelId(folder.id === ROOT_CRUMB.id ? null : folder.id)
  }

  const listHeader = (title: string, sub: string) => (
    <div style={{ padding: '16px 14px 10px', flexShrink: 0 }}>
      <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, lineHeight: 1.25, color: S.text }}>{title}</h2>
      <div style={{ marginTop: 2, fontSize: 13.5, color: S.text2 }}>{sub}</div>
    </div>
  )
  const listBody = { flex: 1, minHeight: 0, overflow: 'auto', padding: '4px 10px 16px' } as const

  const panel = (
    <nav aria-label="Navigation" style={{
      width: compact ? 272 : 308, flexShrink: 0, display: 'flex', flexDirection: 'column',
      minHeight: 0, overflow: 'hidden', background: S.surface2, borderRight: `1px solid ${S.border}`,
    }}>
      <div role="tablist" aria-label="Navigation view" onKeyDown={onTabKey} style={{
        // Each view sized to its label + count (equal columns truncated them).
        display: 'flex', gap: 2,
        margin: '14px 14px 0', padding: 3, borderRadius: 10, background: S.panel,
        border: `1px solid ${S.border}`, flexShrink: 0,
      }}>
        {tabs.map((t) => {
          const on = shownView === t.id
          return (
            <button key={t.id} id={tabId(t.id)} type="button" role="tab" aria-selected={on}
              aria-controls={panelId} tabIndex={on ? 0 : -1} onClick={() => setView(t.id)}
              {...tw('action', `shell-flat-view:${t.id}`, { selected: on })}
              style={{
                flex: '1 0 auto', minWidth: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                height: 38, padding: '0 4px', border: 'none', borderRadius: 7, cursor: 'pointer',
                fontFamily: 'inherit', fontSize: 13.5, fontWeight: on ? 700 : 500,
                background: on ? S.accentWeak : 'transparent', color: on ? S.text : S.text2,
                boxShadow: on ? `inset 0 -3px 0 ${S.accent}` : 'none',
              }}>
              <span className="tw-sh-clip">{t.label}</span>
              {typeof t.count === 'number' && t.count > 0 && (
                <span style={{
                  minWidth: 20, height: 20, padding: '0 5px', boxSizing: 'border-box', borderRadius: 10,
                  background: S.surface2, color: S.text, fontSize: 13, fontWeight: 700, flexShrink: 0,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                }}>{t.count}</span>
              )}
            </button>
          )
        })}
      </div>
      <div id={panelId} role="tabpanel" aria-labelledby={tabId(shownView)}
        style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {shownView === 'menu' && (
          <LevelView model={model} appTitle={appTitle} level={level} onLevel={setLevelId}
            emptyNavMessage={emptyNavMessage} />
        )}
        {shownView === 'pinned' && (
          <>
            {listHeader('Pinned', 'Screens you keep close at hand')}
            <div style={listBody}><PinnedList model={model} /></div>
          </>
        )}
        {shownView === 'recent' && (
          <>
            {listHeader('Recently opened', 'Latest first')}
            <div style={listBody}><RecentList model={model} /></div>
          </>
        )}
        {shownView === 'editors' && (
          <>
            {listHeader('Open editors', 'Records you started and minimised')}
            <div style={listBody}><EditorsList model={model} /></div>
          </>
        )}
      </div>
    </nav>
  )

  return (
    // One tree whether or not full screen is on, so the ShellFrame (and the
    // screen inside it) is never remounted by the toggle.
    <div style={{ display: 'flex', flex: 1, width: '100%', height: '100%', minHeight: 0, overflow: 'hidden' }}>
      {!full && panel}
      <main style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0, background: S.bg }}>
        {current && !full && (
          <ScreenTitle
            item={current}
            height={compact ? 56 : 68}
            compact={compact}
            pinned={model.isPinned(current)}
            onTogglePin={() => model.togglePin(current)}
            fullScreen={full}
            onToggleFullScreen={() => model.setFullScreen(!full)}
            onClose={model.close}
            extras={headerExtras?.(current)}
            subtitle={<Breadcrumb path={[ROOT_CRUMB, ...model.pathOf(current)]} current={current} onFolder={showFolder} />}
          />
        )}
        <ShellFrame
          model={model}
          renderObject={renderObject}
          padding={compact ? '0.5rem' : '0.5rem 1rem 1rem'}
          empty={<EmptyPane model={model} appTitle={appTitle} message={emptyPaneMessage} accent={themeColor} />}
        />
        {!full && model.editorsEnabled && model.editors.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'stretch', flexShrink: 0, background: S.panel, borderTop: `1px solid ${S.border}` }}>
            <button type="button" className="tw-sh-btn" onClick={() => setView('editors')}
              aria-label="Show open editors in the panel" title="Show open editors in the panel"
              aria-pressed={shownView === 'editors'}
              {...tw('action', 'shell-flat-editors-panel', { selected: shownView === 'editors' })}
              style={{ ...iconButton(44), height: 'auto', alignSelf: 'stretch', borderRadius: 0, borderRight: `1px solid ${S.border}` }}>
              <PanelLeft size={18} aria-hidden />
            </button>
            <EditorsTray model={model} style={{ flex: 1, minWidth: 0, borderTop: 'none' }} />
          </div>
        )}
      </main>
    </div>
  )
}
